import { useState, useEffect } from 'react'
import { X, Search, Plus } from 'lucide-react'
import { apiClient } from '@/lib/api'

interface Skill {
  id: number
  skill_name: string
  category: string
  description?: string
}

interface SelectedSkill {
  id: number
  skill_name: string
  category: string
  proficiency_level: 'beginner' | 'intermediate' | 'advanced' | 'expert'
  is_good_to_have?: boolean
}

interface SkillsSelectorProps {
  selectedSkills: SelectedSkill[]
  onSkillsChange: (skills: SelectedSkill[]) => void
  placeholder?: string
  error?: string
}

export default function SkillsSelector({ 
  selectedSkills, 
  onSkillsChange, 
  placeholder = "Search and select skills...",
  error 
}: SkillsSelectorProps) {
  const [allSkills, setAllSkills] = useState<Skill[]>([])
  const [skillsByCategory, setSkillsByCategory] = useState<Record<string, Skill[]>>({})
  const [categories, setCategories] = useState<string[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<string>('')

  useEffect(() => {
    loadSkills()
  }, [])

  useEffect(() => {
    organizeSkillsByCategory()
  }, [allSkills])

  const loadSkills = async () => {
    try {
      setLoading(true)
      const response = await apiClient.getSkills()
      if (response.success && response.data) {
        setAllSkills(response.data)
      }
    } catch (error) {
      console.error('Failed to load skills:', error)
    } finally {
      setLoading(false)
    }
  }

  const organizeSkillsByCategory = () => {
    const grouped = allSkills.reduce((acc, skill) => {
      if (!acc[skill.category]) {
        acc[skill.category] = []
      }
      acc[skill.category].push(skill)
      return acc
    }, {} as Record<string, Skill[]>)

    // Sort skills within each category
    Object.keys(grouped).forEach(category => {
      grouped[category].sort((a, b) => a.skill_name.localeCompare(b.skill_name))
    })

    setSkillsByCategory(grouped)
    setCategories(Object.keys(grouped).sort())
  }

  const toggleSkill = (skill: Skill, isChecked: boolean) => {
    if (isChecked) {
      const newSkill: SelectedSkill = {
        id: skill.id,
        skill_name: skill.skill_name,
        category: skill.category,
        proficiency_level: 'intermediate'
      }
      onSkillsChange([...selectedSkills, newSkill])
    } else {
      onSkillsChange(selectedSkills.filter(selected => selected.id !== skill.id))
    }
  }

  const removeSkill = (skillId: number) => {
    onSkillsChange(selectedSkills.filter(skill => skill.id !== skillId))
  }

  const updateProficiency = (skillId: number, proficiency: SelectedSkill['proficiency_level']) => {
    onSkillsChange(
      selectedSkills.map(skill =>
        skill.id === skillId ? { ...skill, proficiency_level: proficiency } : skill
      )
    )
  }

  const updateGoodToHave = (skillId: number, isGoodToHave: boolean) => {
    onSkillsChange(
      selectedSkills.map(skill =>
        skill.id === skillId ? { ...skill, is_good_to_have: isGoodToHave } : skill
      )
    )
  }


  const isSkillSelected = (skillId: number) => {
    return selectedSkills.some(selected => selected.id === skillId)
  }

  const getProficiencyColor = (level: string) => {
    switch (level) {
      case 'beginner': return 'bg-gray-100 text-gray-800'
      case 'intermediate': return 'bg-blue-100 text-blue-800'
      case 'advanced': return 'bg-green-100 text-green-800'
      case 'expert': return 'bg-purple-100 text-purple-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getCategoryColor = (category: string) => {
    const colors = {
      'Programming Languages': 'bg-red-100 text-red-800',
      'Frontend': 'bg-blue-100 text-blue-800',
      'Backend': 'bg-green-100 text-green-800',
      'Database': 'bg-yellow-100 text-yellow-800',
      'Cloud': 'bg-purple-100 text-purple-800',
      'DevOps': 'bg-orange-100 text-orange-800',
      'Mobile': 'bg-pink-100 text-pink-800',
      'Data Science': 'bg-indigo-100 text-indigo-800'
    }
    return colors[category as keyof typeof colors] || 'bg-gray-100 text-gray-800'
  }

  return (
    <div className="space-y-6">
      {/* Selected Skills */}
      {selectedSkills.length > 0 && (
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700">
            Selected Skills ({selectedSkills.length})
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {selectedSkills.map((skill) => (
              <div
                key={skill.id}
                className={`p-3 border rounded-lg ${
                  skill.is_good_to_have 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-red-50 border-red-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-gray-900">{skill.skill_name}</span>
                    <span className={`px-2 py-1 text-xs rounded-full ${getCategoryColor(skill.category)}`}>
                      {skill.category}
                    </span>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      skill.is_good_to_have 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {skill.is_good_to_have ? 'Good to have' : 'Required'}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeSkill(skill.id)}
                    className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
                
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <label className="text-sm text-gray-700">Proficiency:</label>
                      <select
                        value={skill.proficiency_level}
                        onChange={(e) => updateProficiency(skill.id, e.target.value as SelectedSkill['proficiency_level'])}
                        className="text-xs border-gray-300 rounded px-2 py-1"
                      >
                        <option value="beginner">Beginner</option>
                        <option value="intermediate">Intermediate</option>
                        <option value="advanced">Advanced</option>
                        <option value="expert">Expert</option>
                      </select>
                    </div>
                  </div>
                  
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={skill.is_good_to_have || false}
                      onChange={(e) => updateGoodToHave(skill.id, e.target.checked)}
                      className="form-checkbox text-green-600"
                    />
                    <span className="text-sm text-gray-700">Good to have</span>
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Skills by Category */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Skills by Category
        </label>
        
        {loading ? (
          <div className="text-center py-8">
            <div className="text-gray-500">Loading skills...</div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Category Dropdown */}
            <div className="flex items-center space-x-4">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Choose a category...</option>
                {categories.map(category => {
                  const categorySkills = skillsByCategory[category] || []
                  const selectedCount = categorySkills.filter(skill => isSkillSelected(skill.id)).length
                  return (
                    <option key={category} value={category}>
                      {category} ({categorySkills.length} skills{selectedCount > 0 ? `, ${selectedCount} selected` : ''})
                    </option>
                  )
                })}
              </select>
            </div>

            {/* Skills Grid for Selected Category */}
            {selectedCategory && selectedCategory !== 'all' && (
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 text-sm rounded-full ${getCategoryColor(selectedCategory)}`}>
                      {selectedCategory}
                    </span>
                    <span className="text-sm text-gray-600">
                      {skillsByCategory[selectedCategory]?.length || 0} skills available
                    </span>
                    {(() => {
                      const selectedCount = (skillsByCategory[selectedCategory] || []).filter(skill => isSkillSelected(skill.id)).length
                      return selectedCount > 0 && (
                        <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                          {selectedCount} selected
                        </span>
                      )
                    })()}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {(skillsByCategory[selectedCategory] || []).map((skill) => {
                    const isSelected = isSkillSelected(skill.id)
                    
                    return (
                      <label
                        key={skill.id}
                        className={`flex items-start space-x-3 p-3 rounded-lg cursor-pointer border transition-colors ${
                          isSelected 
                            ? 'bg-blue-50 border-blue-200 text-blue-900' 
                            : 'hover:bg-gray-50 border-gray-200'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => toggleSkill(skill, e.target.checked)}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium">
                            {skill.skill_name}
                          </div>
                          {skill.description && (
                            <div className="text-xs text-gray-500 mt-1 line-clamp-2">
                              {skill.description}
                            </div>
                          )}
                        </div>
                      </label>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Show message when no category selected */}
            {(!selectedCategory || selectedCategory === 'all') && (
              <div className="text-center py-8 text-gray-500">
                <div className="text-sm">Select a category above to see available skills</div>
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
    </div>
  )
}